import React, {useEffect, useRef} from "react";
import {Box, Typography} from "@mui/material";
import * as LeafletNS from "leaflet";
import "leaflet/dist/leaflet.css";
import {GeoCity, GeoCountry} from "../../types/Geo";
import "./OperatingMaps.css";

const L = ((LeafletNS as unknown as {default?: typeof LeafletNS}).default || LeafletNS) as typeof LeafletNS;

type LngLat = [number, number];

const COUNTRY_SHAPES: Record<string, LngLat[]> = {
    BY: [
        [23.2, 51.3], [26.1, 51.25], [30.6, 51.5], [32.7, 52.1], [32.8, 53.1],
        [31.8, 55.2], [30.4, 56.15], [28.6, 56.17], [25.7, 56.05], [23.6, 55.2],
        [23.18, 53.9], [23.2, 51.3],
    ],
    GE: [
        [40.0, 43.4], [40.4, 43.58], [41.7, 43.38], [43.5, 42.98], [44.2, 42.4],
        [45.8, 42.05], [46.7, 41.27], [45.0, 41.15], [43.25, 41.05], [41.55, 41.45],
        [40.32, 41.52], [40.0, 43.4],
    ],
};

const TILE_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

function toLatLngs(ring: LngLat[]): L.LatLngExpression[] {
    return ring.map(([lng, lat]) => [lat, lng]);
}

function addBaseTiles(map: L.Map) {
    if (typeof L.tileLayer !== "function") {
        return;
    }
    const layer = L.tileLayer(TILE_URL, {
        attribution: TILE_ATTR,
        subdomains: "abcd",
        maxZoom: 19,
    });
    if (layer) {
        layer.addTo(map);
    }
}

function useLeafletMap(
    containerRef: React.RefObject<HTMLDivElement | null>,
    setup: (map: L.Map) => void,
    deps: React.DependencyList,
) {
    const setupRef = useRef(setup);
    setupRef.current = setup;
    useEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return;
        }
        let map: L.Map | undefined;
        let timer: number | undefined;
        try {
            map = L.map(node, {scrollWheelZoom: true, worldCopyJump: true});
            addBaseTiles(map);
            setupRef.current(map);
            timer = window.setTimeout(() => map && map.invalidateSize(), 80);
        } catch {
            return undefined;
        }
        return () => {
            if (timer) {
                window.clearTimeout(timer);
            }
            map?.remove();
        };
        // setup is read from a ref so the map is not rebuilt on every parent render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

export function WorldOperatingMap({countries, onSelectCountry}: {
    countries: GeoCountry[];
    onSelectCountry: (country: GeoCountry) => void;
}) {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const rows = Array.isArray(countries) ? countries : [];
    const countryKey = rows.map((country) => country.id + ":" + country.iso2).join(",");

    useLeafletMap(mapRef, (map) => {
        const points: L.LatLngExpression[] = [];
        rows.forEach((country) => {
            const ring = COUNTRY_SHAPES[country.iso2];
            if (ring) {
                const latlngs = toLatLngs(ring);
                latlngs.forEach((point) => points.push(point));
                L.polygon(latlngs, {
                    color: "#1b5e20",
                    weight: 2,
                    fillColor: "#2e7d32",
                    fillOpacity: 0.45,
                })
                    .addTo(map)
                    .on("click", () => onSelectCountry(country))
                    .bindTooltip(country.name + " · " + country.listingCount + " listings");
                return;
            }
            const cities = (country.cities || []).filter((city) => city.lat != null && city.lng != null);
            if (!cities.length) {
                return;
            }
            const lat = cities.reduce((sum, city) => sum + (city.lat as number), 0) / cities.length;
            const lng = cities.reduce((sum, city) => sum + (city.lng as number), 0) / cities.length;
            points.push([lat, lng]);
            L.circleMarker([lat, lng], {radius: 10, color: "#1b5e20", fillColor: "#2e7d32", fillOpacity: 0.8, weight: 2})
                .addTo(map)
                .on("click", () => onSelectCountry(country))
                .bindTooltip(country.name + " · " + country.listingCount + " listings");
        });
        if (points.length) {
            map.fitBounds(L.latLngBounds(points), {padding: [28, 28], maxZoom: 6});
        } else {
            map.setView([42, 32], 4);
        }
    }, [countryKey]);

    return (
        <Box className="operating-map-shell" data-testid="operating-world-map">
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                Political map of countries where Relapka currently operates. Click a highlighted country.
            </Typography>
            <div ref={mapRef} className="operating-leaflet-map" role="img" aria-label="World political map"/>
            {rows.map((country) => (
                <button
                    key={country.id}
                    type="button"
                    className="operating-map-hit"
                    data-testid={"country-shape-" + country.iso2}
                    onClick={() => onSelectCountry(country)}
                >
                    {country.name}
                </button>
            ))}
        </Box>
    );
}

export function CountryCitiesMap({country, onSelectCity}: {
    country: GeoCountry;
    onSelectCity: (city: GeoCity) => void;
}) {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const cities = country.cities || [];
    const cityKey = cities.map((city) => city.id + ":" + city.listingCount).join(",");

    useLeafletMap(mapRef, (map) => {
        const points: L.LatLngExpression[] = [];
        const ring = COUNTRY_SHAPES[country.iso2];
        if (ring) {
            const latlngs = toLatLngs(ring);
            latlngs.forEach((point) => points.push(point));
            L.polygon(latlngs, {
                color: "#1b5e20",
                weight: 2,
                fillColor: "#8bc48f",
                fillOpacity: 0.35,
            }).addTo(map);
        }
        cities.forEach((city) => {
            if (city.lat == null || city.lng == null) {
                return;
            }
            const size = 24 + Math.min(18, Math.sqrt(city.listingCount || 0) / 4);
            points.push([city.lat, city.lng]);
            const icon = L.divIcon({
                className: "city-map-marker",
                html: '<div class="city-map-marker-inner">' + (city.listingCount || 0) + "</div>",
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2],
            });
            L.marker([city.lat, city.lng], {icon, title: city.name})
                .addTo(map)
                .on("click", () => onSelectCity(city))
                .bindTooltip(city.name, {permanent: true, direction: "right", offset: [size / 2 + 4, 0], className: "city-map-label"});
        });
        if (points.length) {
            map.fitBounds(L.latLngBounds(points), {padding: [36, 36], maxZoom: 8});
        } else {
            map.setView([42, 32], 5);
        }
    }, [country.id, country.iso2, cityKey]);

    return (
        <Box className="operating-map-shell" data-testid="country-cities-map">
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                {country.name}: cities currently in Relapka. Click a city to open its dashboard.
            </Typography>
            <div ref={mapRef} className="operating-leaflet-map" role="img" aria-label={country.name + " political map"}/>
            {cities.map((city) => (
                <button
                    key={city.id}
                    type="button"
                    className="operating-map-hit"
                    data-testid={"city-marker-" + city.id}
                    onClick={() => onSelectCity(city)}
                >
                    {city.name}
                </button>
            ))}
        </Box>
    );
}
